/**
 * A text value edited by the world-space keyboard (or the physical keyboard on desktop) and
 * drawn into a canvas panel as a wrapped, scrolling block with a caret.
 */
import { PALETTE, fillRoundRect, font, text, wrapText, type Rect } from '../dashboards/charts.ts';

export class TextField {
  value = '';
  /** Dictation transcript not yet committed. */
  interim = '';

  input(ch: string): void {
    this.value += ch;
  }

  backspace(): void {
    this.value = this.value.slice(0, -1);
  }

  clear(): void {
    this.value = '';
    this.interim = '';
  }

  get trimmed(): string {
    return this.value.trim();
  }
}

export function drawTextField(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  field: TextField,
  opts: { placeholder?: string; active?: boolean; size?: number } = {},
): void {
  const size = opts.size ?? 13;
  fillRoundRect(ctx, r, 8, opts.active ? PALETTE.field : PALETTE.fieldIdle);
  if (opts.active) {
    ctx.strokeStyle = PALETTE.teal;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2, 8);
    ctx.stroke();
  }
  ctx.font = font(size);
  const lineH = Math.round(size * 1.15 * 1.45);
  const maxLines = Math.max(1, Math.floor((r.h - 16) / lineH));
  const lines = field.value ? wrapText(ctx, field.value, r.w - 24) : [];
  if (field.interim) lines.push(...wrapText(ctx, field.interim, r.w - 24).map((l) => `\u0001${l}`));
  const shown = lines.slice(-maxLines);
  if (shown.length === 0 && opts.placeholder) {
    text(ctx, opts.placeholder, r.x + 12, r.y + 10 + lineH - 4, { size, colour: PALETTE.muted });
  }
  let caretX = r.x + 12;
  let caretY = r.y + 10 + lineH - 4;
  shown.forEach((raw, i) => {
    const interim = raw.startsWith('\u0001');
    const l = interim ? raw.slice(1) : raw;
    const y = r.y + 10 + lineH * (i + 1) - 4;
    text(ctx, l, r.x + 12, y, { size, colour: interim ? PALETTE.muted : PALETTE.text });
    if (!interim) {
      ctx.font = font(size);
      caretX = r.x + 12 + ctx.measureText(l).width + 2;
      caretY = y;
    }
  });
  if (opts.active) {
    ctx.fillStyle = PALETTE.teal;
    ctx.fillRect(caretX, caretY - size * 1.15 + 2, 2, size * 1.15 + 2);
  }
}
